import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import { PHASE_LABEL_KEYS } from '@/consts/phases';

/**
 * Boris's per-screen phase bar (register.css `.rg-phasebar` / `RPhaseBar`):
 * a "Step n of 4" mono eyebrow over a single gold bar filled to n/4. Shown at
 * the top of the desktop main column on flow steps (the screeners use their own
 * per-question dashes instead). The 4 phases mirror `phases.ts` / Boris's
 * DesktopFrame side-rail. Decorative bar (aria-hidden); the label conveys
 * progress to assistive tech.
 */
type Props = { phase: number };

const TOTAL = PHASE_LABEL_KEYS.length; // 4

const Wrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// Boris register.css `.reg-dt-main .rg-phasebar-step`: mono 12px, ls .08em,
// uppercase, ink-soft. Desktop-only per Boris (`.rg-phasebar-step{display:none}`
// on mobile) — but kept in the DOM and only visually hidden on mobile so it
// still conveys progress to assistive tech (the bar itself is aria-hidden).
const Label = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${BorisScreener.inkSoft};
  @media (max-width: 899px) {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
`;

const Track = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: ${BorisScreener.dashTrack};
  overflow: hidden;
`;

const Fill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${BorisScreener.dashFill};
  border-radius: 999px;
  transition: width 0.3s ease;
`;

const PhaseProgressBar = ({ phase }: Props) => {
  const { t } = useTranslation();
  const n = Math.min(TOTAL, Math.max(1, phase + 1));
  const pct = (n / TOTAL) * 100;
  return (
    <Wrap>
      <Label>{t('reg-phase.step', { n, total: TOTAL })}</Label>
      <Track>
        <Fill $pct={pct} aria-hidden="true" />
      </Track>
    </Wrap>
  );
};

export { PhaseProgressBar };
